import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { AuthenticationService } from '@common/services';
import { CoreMenuService } from '@core/components/core-menu/core-menu.service';

import { CustomSwalService } from './custom-swal.service';
import { HANDLES_OWN_ERRORS } from './http-context';

import { environment } from 'environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { InsightsService } from '@core/services';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor, OnDestroy {

  private _unsubscribeAll: Subject<any>;
  
  /**
   * Constructor
   * @param {Router} _router 
   * @param {AuthenticationService} _authenticationService 
   * @param {CoreMenuService} _coreMenuService 
   * @param {TranslateService} _translateService 
   * @param {CustomSwalService} _swalService 
   * @param {InsightsService} _insightsService 
   */
  constructor(
    private _router: Router, 
    private _authenticationService: AuthenticationService,
    private _coreMenuService: CoreMenuService,
    private _translateService: TranslateService,
    private _swalService: CustomSwalService,
    private _insightsService: InsightsService) {
      this._unsubscribeAll = new Subject();
    }


  /**
   * On Subscribe
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError(err => {
        // La petición se hace cargo de sus propios errores: ni modal, ni
        // excepción en insights, y se propaga la respuesta original para que
        // quien llamó pueda mirar el status
        if (request.context.get(HANDLES_OWN_ERRORS)) {
          if (!environment.production) console.log(err);

          return throwError(() => err);
        }

        let error: string =  err.error != null ? (err.error?.message ?? err.error?.errors?.join(", ") ?? err.error?.error?.message ?? err.message ?? '') : null;
        // Delimitamos que los mensajes de error solo sean únicamente cuando son de nuestras propias apis
        if (err.url?.startsWith(environment.apiUrl)){
          switch(err.status){
            case 400:
              if(error){
                this._translateService.get(error).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
                  this._swalService.showErrorMsg(value);
                });
              }else{
                this._translateService.get(['COMMON.ERROR_INTECEPTOR.NOT_FOUND', 'COMMON.ERROR_INTECEPTOR.NOT_FOUND_MESSAGE']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
                  this._swalService.showWarningMsg(value['COMMON.ERROR_INTECEPTOR.NOT_FOUND_MESSAGE'], value['COMMON.ERROR_INTECEPTOR.NOT_FOUND']);
                });
              }           
              break;
            case 401:
              // Un 401 obsoleto es esperable y no significa falta de permisos:
              // expulsar al usuario aquí lo mandaba a la página de no autorizado
              // al iniciar sesión o al recargar la aplicación
              if (this.isStaleUnauthorized(request)){
                break;
              }
              // auto logout if 401 Unauthorized
              if (this._authenticationService.isAuthenticated()){
                this._router.navigate(['/pages/miscellaneous/not-authorized']);
                const menu = this._coreMenuService.getCurrentMenu();
                if(!menu || menu.length == 0){
                  this._coreMenuService.unregister('main');
                  this._authenticationService.logout();
                }
              } else {
                this._translateService.get(['PAGES.LOGIN.MESSAGES.ACCESS_DENIED']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
                  error = value['PAGES.LOGIN.MESSAGES.ACCESS_DENIED'];
                });
              }
              break;
            case 403:
              this._translateService.get(['COMMON.ERROR_INTECEPTOR.ACCESS_DENIED', 'COMMON.ERROR_INTECEPTOR.ACCESS_DENIED_MESSAGE']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
                this._swalService.showWarningMsg(value['COMMON.ERROR_INTECEPTOR.ACCESS_DENIED_MESSAGE'], value['COMMON.ERROR_INTECEPTOR.ACCESS_DENIED']);
              });
              break;
            case 404:
                this._translateService.get(['COMMON.ERROR_INTECEPTOR.NOT_FOUND', 'COMMON.ERROR_INTECEPTOR.NOT_FOUND_MESSAGE']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
                  this._swalService.showWarningMsg(value['COMMON.ERROR_INTECEPTOR.NOT_FOUND_MESSAGE'], value['COMMON.ERROR_INTECEPTOR.NOT_FOUND']);
                });          
                break;
            case 500:
              this._translateService.get(['COMMON.ERROR_INTECEPTOR.INTERNAL_SERVER_ERROR']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
                this._swalService.showErrorMsg(value['COMMON.ERROR_INTECEPTOR.INTERNAL_SERVER_ERROR']);
              });
              break;
            case 503:
              this._translateService.get(['COMMON.ERROR_INTECEPTOR.SERVICE_UNAVAILABLE']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
                this._swalService.showErrorMsg(value['COMMON.ERROR_INTECEPTOR.SERVICE_UNAVAILABLE']);
              });
              break;
            case 504:
              this._translateService.get(['COMMON.ERROR_INTECEPTOR.SERVICE_TIMEOUT', 'COMMON.ERROR_INTECEPTOR.SERVICE_TIMEOUT_MESSAGE']).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
                this._swalService.showErrorMsg(value['COMMON.ERROR_INTECEPTOR.SERVICE_TIMEOUT_MESSAGE'], value['COMMON.ERROR_INTECEPTOR.SERVICE_TIMEOUT']);
              });
              break;
            default:
              this._translateService.get(error).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
                this._swalService.showErrorMsg(value);
              });
              break;
          }
        }
        if (!environment.production) console.log(err);

        // Mapea la excepción en el application insights
        this._insightsService.logException({name: `Exception - ${err.status}`, message: error, stack: JSON.stringify(err)});
        // throwError
        return throwError(error);
      })
    );
  }

  /**
   * Indica si un 401 quedó obsoleto, es decir si la sesión con la que viajó la
   * petición ya no es la actual.
   *
   * Se evalúa contra el token que llevaba la petición y no solo contra el estado
   * del momento, porque la respuesta puede llegar cuando la renovación ya terminó:
   * al iniciar sesión o al recargar, las pantallas piden datos antes de que exista
   * el token y esos 401 no significan que al usuario le falten permisos.
   *
   * @param request Petición que devolvió el 401
   */
  private isStaleUnauthorized(request: HttpRequest<any>): boolean {
    // Sin sesión el 401 viene del propio intento de autenticación, no está obsoleto
    if (!this._authenticationService.isAuthenticated()) return false;

    // La sesión se está renovando o el token ya venció
    if (this._authenticationService.isRenewingSession
      || this._authenticationService.isTokenExpired()) return true;

    // La petición salió sin token, o con uno distinto al actual porque la sesión
    // se renovó mientras viajaba
    return request.headers.get('Authorization') !== `Bearer ${this._authenticationService.token}`;
  }
}
